import { useState } from 'react'
import { Link } from 'react-router'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { useCurrentUser } from '@/auth/context'
import { addressInitials, relativeTime } from '@/lib/format'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Field, TextField } from '@/ui/Field'
import { Modal } from '@/ui/Modal'
import { ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

/**
 * The roles a tenant can define, and the pages each one grants (SHELL-26).
 *
 * **This screen used to draw a fixed matrix of ticks** — Owner, Admin, Member
 * down one axis and seven capabilities down the other, rendered from the
 * client's mirror of the permission table. S-D3 drew four columns where the
 * model had three, and the audit kept handing the mismatch back.
 *
 * The owner settled it on 2026-09-09 by removing the axis: there are two
 * predefined roles and everything else is a role this organisation created, so
 * the table is now the organisation's own roles against the pages they reach —
 * a thing the reader can change rather than a thing they can only read.
 */

type RoleRow = Schema<'RoleOut'>
type PageRow = Schema<'app__v1__tenancy__PageOut'>

/**
 * The value the role picker uses for *administrator*.
 *
 * A sentinel rather than an id, because the administrator is a **kind** of
 * membership and not one of the organisation's roles — there is no row to point
 * at. A `<select>` needs some string, and one that could never be a UUID is what
 * keeps the two cases apart without a second control beside it.
 */
const ADMIN_VALUE = 'tenant_admin'

/** What a member's role is called, for a cell that cannot edit it. */
/**
 * What the administrator kind is called on this screen: *Owner*, the word
 * `shell.html` prints on the brand badge (parity P-TEAM-03, 2026-09-13). It is
 * the one membership that is not one of the organisation's own roles.
 */
const ADMIN_LABEL = 'Owner'

function roleName(row: { role: string; role_id?: string | null }, roles: RoleRow[]): string {
  if (row.role === 'tenant_admin') return ADMIN_LABEL
  return roles.find((option) => option.id === row.role_id)?.name ?? 'Role'
}

/**
 * Team and roles.
 *
 * The role is chosen with its permissions visible rather than from a bare
 * dropdown (S-D3) — most wrong-permission incidents come from someone picking
 * "Admin" because it sounded standard.
 *
 * **An invitation cannot confer ownership.** That is enforced on the server
 * (`INVITABLE_ROLES`), and the reason is worth knowing: an emailed link that
 * could grant ownership would make the mailbox it was sent to equivalent to the
 * organisation.
 *
 * `email` is rendered with a fallback because it is **nullable** on every
 * response carrying a user: §4 permits an account created with a mobile number
 * and no address, and a screen printing `user.email` unconditionally would show
 * `null` for one.
 */
export function TeamPanel(): JSX.Element {
  const { tenant, membership } = useActiveTenant()
  const { user } = useCurrentUser()
  const toast = useToast()
  const [editingRole, setEditingRole] = useState<RoleRow | 'new' | null>(null)

  const canInvite = may(membership, 'members:invite')
  const canRemove = may(membership, 'members:remove')
  const canChangeRole = may(membership, 'members:role')

  const roles = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/tenants/{tenant_id}/roles', {
          params: { path: { tenant_id: tenant.id } },
          signal,
        }),
      ),
    [tenant.id],
  )

  const members = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/tenants/{tenant_id}/members', {
          params: { path: { tenant_id: tenant.id } },
          signal,
        }),
      ),
    [tenant.id],
  )

  const invitations = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/tenants/{tenant_id}/invitations', {
          params: { path: { tenant_id: tenant.id } },
          signal,
        }),
      ),
    [tenant.id],
  )

  const changeRole = useMutation((input: { userId: string; value: string }) =>
    call(() =>
      api.PATCH('/api/v1/tenants/{tenant_id}/members/{user_id}', {
        params: { path: { tenant_id: tenant.id, user_id: input.userId } },
        // SHELL-26. One control, two shapes: the administrator is a *kind* of
        // membership and everything else is one of the organisation's roles, so
        // the option's value carries which of the two it is.
        body:
          input.value === ADMIN_VALUE
            ? { role: 'tenant_admin' as const }
            : { role: 'custom' as const, role_id: input.value },
      }),
    ),
  )

  const deleteRole = useMutation((roleId: string) =>
    call(() =>
      api.DELETE('/api/v1/tenants/{tenant_id}/roles/{role_id}', {
        params: { path: { tenant_id: tenant.id, role_id: roleId } },
      }),
    ),
  )

  const removeMember = useMutation((userId: string) =>
    call(() =>
      api.DELETE('/api/v1/tenants/{tenant_id}/members/{user_id}', {
        params: { path: { tenant_id: tenant.id, user_id: userId } },
      }),
    ),
  )

  const revoke = useMutation((invitationId: string) =>
    call(() =>
      api.DELETE('/api/v1/tenants/{tenant_id}/invitations/{invitation_id}', {
        params: { path: { tenant_id: tenant.id, invitation_id: invitationId } },
      }),
    ),
  )

  const resend = useMutation((invitationId: string) =>
    call(() =>
      api.POST('/api/v1/tenants/{tenant_id}/invitations/{invitation_id}/resend', {
        params: { path: { tenant_id: tenant.id, invitation_id: invitationId } },
      }),
    ),
  )

  function reloadAll(): void {
    members.refetch()
    invitations.refetch()
    roles.refetch()
  }

  const now = new Date()
  const catalogue = roles.state.data?.pages ?? []
  const roleList = roles.state.data?.roles ?? []
  const seats = members.state.data?.seats ?? invitations.state.data?.seats ?? null
  const pending = invitations.state.data?.invitations ?? []

  return (
    <div className="ar-stack ar-stack--loose">
      <section className="ar-card">
        <div className="ar-card__header">
          <h2>Team &amp; roles</h2>
          {seats ? (
            <span className="ar-badge ar-badge--neutral">
              {seats.used} {seats.limit === null ? 'in use' : `of ${String(seats.limit)} seats`}
            </span>
          ) : null}
          <span className="ar-card__spacer" />
          {/* Parity P-INV-01: `shell#invite` is a screen of its own, so the
              button is a link to it rather than the opener of a dialog. */}
          {canInvite ? (
            <Link className="ar-btn ar-btn--primary ar-btn--sm" to="/settings/team/invite">
              Invite someone
            </Link>
          ) : null}
        </div>

        <InlineError
          failure={
            changeRole.state.failure ??
            removeMember.state.failure ??
            revoke.state.failure ??
            resend.state.failure
          }
        />

        {members.state.status === 'loading' ? (
          <div className="ar-card__body">
            <Loading label="Loading the team" lines={3} />
          </div>
        ) : null}

        {members.state.status === 'error' ? (
          <ErrorState
            failure={members.state.failure}
            title="Could not load the team"
            onRetry={members.refetch}
          />
        ) : null}

        {members.state.status === 'ready' ? (
          <div className="ar-table-wrap" tabIndex={0}>
            <table className="ar-table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Role</th>
                  {/* SHELL-37. S-D3 asks "Last active" and shows it relative —
                      "Now", "2 hours ago". `last_login_at` is the only signal
                      the API has, so the column is honest about being a sign-in
                      rather than promising activity it cannot see. */}
                  <th>Last active</th>
                  <th>
                    <span className="ar-visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {members.state.data.members.map((row) => {
                  const self = row.user_id === user.id
                  return (
                    <tr key={row.user_id}>
                      <td>
                        {/*
                          SHELL-28 and SHELL-38. S-D3's person cell is an avatar,
                          then a display name with the address beneath it.
                          **There is no display name to show**: `users` stores an
                          email or a mobile and no name at all, so the address
                          takes the strong line and the caption carries what the
                          row actually knows. The avatar is initials derived from
                          the address — see `addressInitials` on why a guess is
                          the right thing there and a wrong one costs nothing.
                        */}
                        <div className="ar-row">
                          <span className="ar-avatar ar-avatar--sm" aria-hidden="true">
                            {addressInitials(row.email, row.mobile)}
                          </span>
                          <div>
                            <strong>{row.email ?? row.mobile ?? 'Account with no address'}</strong>
                            {row.email && !row.email_verified ? (
                              <div
                                className="ar-subtle"
                                style={{ fontSize: 'var(--ar-text-caption-size)' }}
                              >
                                address not confirmed
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td>
                        {canChangeRole && !self ? (
                          <select
                            className="ar-select"
                            aria-label={`Role for ${row.email ?? row.user_id}`}
                            style={{
                              height: 'var(--ar-control-sm)',
                              fontSize: 'var(--ar-text-body-sm-size)',
                            }}
                            value={row.role === 'tenant_admin' ? ADMIN_VALUE : (row.role_id ?? '')}
                            disabled={changeRole.pending}
                            onChange={(event) => {
                              void (async () => {
                                const result = await changeRole.run({
                                  userId: row.user_id,
                                  value: event.target.value,
                                })
                                if (result.ok) {
                                  reloadAll()
                                  toast.show({ kind: 'success', title: 'Role changed' })
                                }
                              })()
                            }}
                          >
                            {/* SHELL-26. The organisation's own roles, plus the
                                one kind of membership that is not a role. The
                                administrator is listed first because it is the
                                only entry whose meaning is fixed. */}
                            <option value={ADMIN_VALUE}>{ADMIN_LABEL}</option>
                            {(roles.state.data?.roles ?? []).map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          // SHELL-39. The administrator's badge is `brand`, not
                          // `neutral`: S-D3 marks the one irreplaceable role on
                          // the screen, and a grey chip identical to every other
                          // role's is the one thing that cell must not be.
                          <span
                            className={`ar-badge ${
                              row.role === 'tenant_admin' ? 'ar-badge--brand' : 'ar-badge--neutral'
                            }`}
                          >
                            {roleName(row, roles.state.data?.roles ?? [])}
                          </span>
                        )}
                      </td>
                      <td className="ar-muted">
                        {row.last_login_at ? relativeTime(row.last_login_at, now) : '—'}
                      </td>
                      <td className="ar-num">
                        {canRemove && !self ? (
                          <Button
                            tone="quiet"
                            size="sm"
                            disabled={removeMember.pending}
                            onClick={() => {
                              void (async () => {
                                const result = await removeMember.run(row.user_id)
                                if (result.ok) {
                                  reloadAll()
                                  toast.show({ kind: 'success', title: 'Member removed' })
                                }
                              })()
                            }}
                          >
                            Remove
                          </Button>
                        ) : self ? (
                          // SHELL-41. "You" said who the row was; the mockup's
                          // caption says why there is no control on it, which is
                          // the question somebody looking at an empty action
                          // cell is actually asking.
                          <span
                            className="ar-subtle"
                            style={{ fontSize: 'var(--ar-text-caption-size)' }}
                          >
                            Can&apos;t change own role
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  )
                })}

                {/*
                  SHELL-42. Pending invitations are rows in the team table, not a
                  card below it. S-D3 draws them that way and it is the truer
                  shape: an invitation **holds a seat**, so the count in the
                  header is a count of these rows plus the members above them —
                  and a separate card made the seat badge look wrong to anybody
                  who added up what they could see.

                  The three-state handling the separate card carried is not lost:
                  a failed invitations read still renders its own error below,
                  and "nobody is waiting" is now the ordinary case of a table
                  with no pending rows rather than an empty state of its own.
                */}
                {pending.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="ar-row">
                        <span className="ar-avatar ar-avatar--sm" aria-hidden="true">
                          —
                        </span>
                        <div>
                          <strong>{row.email}</strong>
                          <div
                            className="ar-subtle"
                            style={{ fontSize: 'var(--ar-text-caption-size)' }}
                          >
                            Invited {relativeTime(row.created_at, now)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="ar-badge ar-badge--neutral">
                        {roleName(row, roles.state.data?.roles ?? [])} · pending
                      </span>
                    </td>
                    <td className="ar-muted">—</td>
                    <td className="ar-num">
                      {canInvite ? (
                        <>
                          {/*
                            SHELL-25. Resend, which the panel did not have: the
                            only recovery from an invitation that never arrived
                            was *withdraw and re-invite*, and on a plan whose
                            seats are full that gives the seat up before taking
                            it back. The server mints a fresh token and keeps the
                            seat — see `resend_invitation`.
                          */}
                          <Button
                            tone="quiet"
                            size="sm"
                            disabled={resend.pending}
                            onClick={() => {
                              void (async () => {
                                const result = await resend.run(row.id)
                                if (!result.ok) return
                                reloadAll()
                                toast.show(
                                  result.data.invitation_email_sent
                                    ? {
                                        kind: 'success',
                                        title: 'Invitation sent again',
                                        body: `${row.email} has a new link, good for seven days. The old one no longer works.`,
                                      }
                                    : {
                                        kind: 'warning',
                                        title: 'New link created, email not sent',
                                        body: 'The invitation is still live. Try again in a moment.',
                                      },
                                )
                              })()
                            }}
                          >
                            Resend
                          </Button>{' '}
                          <Button
                            tone="quiet"
                            size="sm"
                            disabled={revoke.pending}
                            onClick={() => {
                              void (async () => {
                                const result = await revoke.run(row.id)
                                if (result.ok) {
                                  reloadAll()
                                  toast.show({
                                    kind: 'success',
                                    title: 'Invitation withdrawn',
                                    body: 'Its seat is free again.',
                                  })
                                }
                              })()
                            }}
                          >
                            Withdraw
                          </Button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {invitations.state.status === 'error' ? (
          <ErrorState
            failure={invitations.state.failure}
            title="Could not load pending invitations"
            onRetry={invitations.refetch}
          />
        ) : null}
      </section>

      {/*
        SHELL-26 and SHELL-43, redrawn for parity P-TEAM-05 (2026-09-13).

        `shell.html` draws *What each role can do*: a capability down the rows,
        a role across the columns, a tick where the two meet. The owner removed
        the fixed Owner/Admin/Editor/Viewer axis on 2026-09-09 — an organisation
        defines the roles it needs and says which pages each reaches — so the
        columns here are the organisation's own roles, after the owner, and the
        rows are the page catalogue the server sends with them. Nothing the
        roles card carried is lost: *New role* keeps the header, and each role's
        *Edit* and *Delete* sit under its column heading.
      */}
      <section className="ar-card">
        <div className="ar-card__header">
          <h2>What each role can do</h2>
          <span className="ar-card__spacer" />
          {canChangeRole ? (
            <Button
              tone="ghost"
              size="sm"
              onClick={() => {
                setEditingRole('new')
              }}
            >
              New role
            </Button>
          ) : null}
        </div>

        <InlineError failure={deleteRole.state.failure} />

        {roles.state.status === 'loading' ? (
          <div className="ar-card__body">
            <Loading label="Loading roles" lines={2} />
          </div>
        ) : null}

        {roles.state.status === 'error' ? (
          <ErrorState
            failure={roles.state.failure}
            title="Could not load this organisation's roles"
            onRetry={roles.refetch}
          />
        ) : null}

        {roles.state.status === 'ready' ? (
          <div className="ar-table-wrap" tabIndex={0}>
            <table className="ar-table">
              <thead>
                <tr>
                  <th scope="col">Capability</th>
                  <th scope="col">{ADMIN_LABEL}</th>
                  {roleList.map((row) => (
                    <th scope="col" key={row.id}>
                      {row.name}
                      {canChangeRole ? (
                        <div className="ar-row" style={{ gap: 'var(--ar-space-1)' }}>
                          <Button
                            tone="quiet"
                            size="sm"
                            onClick={() => {
                              setEditingRole(row)
                            }}
                          >
                            Edit
                          </Button>
                          {/* Offered only when nobody holds it: the server
                              answers 409 with the count, and a button that
                              always fails is worse than one that is not there. */}
                          {row.members === 0 ? (
                            <Button
                              tone="quiet"
                              size="sm"
                              disabled={deleteRole.pending}
                              onClick={() => {
                                void (async () => {
                                  const result = await deleteRole.run(row.id)
                                  if (result.ok) {
                                    reloadAll()
                                    toast.show({ kind: 'success', title: 'Role deleted' })
                                  }
                                })()
                              }}
                            >
                              Delete
                            </Button>
                          ) : null}
                        </div>
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {catalogue.map((page) => (
                  <tr key={page.key}>
                    <td>{page.label}</td>
                    <td>✓</td>
                    {roleList.map((row) => (
                      <td key={row.id}>
                        {row.pages.includes(page.key) ? (
                          '✓'
                        ) : (
                          <>
                            <span aria-hidden="true">—</span>
                            <span className="ar-visually-hidden">no</span>
                          </>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {editingRole !== null && roles.state.status === 'ready' ? (
        <RoleDialog
          tenantId={tenant.id}
          catalogue={roles.state.data.pages}
          existing={editingRole === 'new' ? null : editingRole}
          onClose={() => {
            setEditingRole(null)
          }}
          onSaved={(name) => {
            setEditingRole(null)
            reloadAll()
            toast.show({ kind: 'success', title: `${name} saved` })
          }}
        />
      ) : null}
    </div>
  )
}

/**
 * Create or edit one role — the control the whole of SHELL-26 exists to offer.
 *
 * **Pages, grouped as the sidebar groups them.** The reader is choosing which
 * screens somebody sees, and the fastest way to get that wrong is a flat list of
 * thirty keys; the modules are the shape they already navigate in.
 *
 * **The whole set is sent, never a patch.** The control is a set of checkboxes,
 * so *untick Approvals* and *tick Approvals* are the same request with a
 * different body — a partial update would need two shapes for one gesture and
 * they would drift.
 */
function RoleDialog({
  tenantId,
  catalogue,
  existing,
  onClose,
  onSaved,
}: {
  tenantId: string
  catalogue: PageRow[]
  existing: RoleRow | null
  onClose: () => void
  onSaved: (name: string) => void
}): JSX.Element {
  const [name, setName] = useState(existing?.name ?? '')
  const [pages, setPages] = useState<string[]>(existing?.pages ?? [])

  const save = useMutation((body: { name: string; pages: string[] }) =>
    call(() =>
      existing === null
        ? api.POST('/api/v1/tenants/{tenant_id}/roles', {
            params: { path: { tenant_id: tenantId } },
            body,
          })
        : api.PUT('/api/v1/tenants/{tenant_id}/roles/{role_id}', {
            params: { path: { tenant_id: tenantId, role_id: existing.id } },
            body,
          }),
    ),
  )

  const modules = catalogue.reduce<Record<string, PageRow[]>>((grouped, page) => {
    grouped[page.module] = [...(grouped[page.module] ?? []), page]
    return grouped
  }, {})

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    const result = await save.run({ name: name.trim(), pages })
    if (result.ok) onSaved(result.data.name)
  }

  return (
    <Modal
      title={existing === null ? 'New role' : `Edit ${existing.name}`}
      onClose={onClose}
      footer={
        <>
          <Button tone="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button tone="primary" type="submit" form="role" writes busy={save.pending}>
            Save role
          </Button>
        </>
      }
    >
      <form
        id="role"
        className="ar-stack"
        onSubmit={(event) => {
          void onSubmit(event)
        }}
      >
        <InlineError failure={save.state.failure} />
        <TextField
          label="Name"
          required
          maxLength={60}
          help="What your organisation calls this role. Editor, Client, Analyst — whatever fits."
          value={name}
          onChange={(event) => {
            setName(event.target.value)
            save.reset()
          }}
        />
        {/* Editing a live role changes what its holders may do on their next
            request. Said here rather than in a confirmation dialog, because it
            is a property of the feature rather than a risk of this one press. */}
        {existing !== null && existing.members > 0 ? (
          <p className="ar-field__help" style={{ margin: 0 }}>
            {existing.members} {existing.members === 1 ? 'person holds' : 'people hold'} this role.
            Changes apply to them immediately.
          </p>
        ) : null}
        {Object.entries(modules).map(([module, entries]) => (
          <Field key={module} label={moduleLabel(module)}>
            {() => (
              <div>
                {entries.map((page) => (
                  <label className="ar-choice" key={page.key}>
                    <input
                      type="checkbox"
                      checked={pages.includes(page.key)}
                      onChange={(event) => {
                        setPages((current) =>
                          event.target.checked
                            ? [...current, page.key]
                            : current.filter((key) => key !== page.key),
                        )
                        save.reset()
                      }}
                    />
                    <span className="ar-choice__box" aria-hidden="true" />
                    <span className="ar-choice__text">{page.label}</span>
                  </label>
                ))}
              </div>
            )}
          </Field>
        ))}
      </form>
    </Modal>
  )
}

/** The module headings on the role editor, in the sidebar's own words. */
function moduleLabel(module: string): string {
  return { outreach: 'Outreach', seo: 'SEO', settings: 'Settings' }[module] ?? 'Money'
}
